/**
 * Day 10 — Review Outcomes Analytics (frontend-only, read-only).
 *
 * Pure aggregation helpers over items already returned by
 * GET /api/participants/charts/review-outcomes. No mutations, no API calls.
 */

function _pad(n) { return n < 10 ? `0${n}` : `${n}`; }

function _localDateKey(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return `${d.getFullYear()}-${_pad(d.getMonth() + 1)}-${_pad(d.getDate())}`;
  } catch { return null; }
}

function _fromDateKey(key) {
  if (!key) return null;
  const [y, m, day] = key.split('-').map(Number);
  return new Date(y, (m || 1) - 1, day || 1);
}

/** Produce a dense series from oldest date to newest so the line chart looks correct. */
export function groupByDate(items) {
  const counts = new Map();
  for (const it of items || []) {
    const key = _localDateKey(it?.review_completed_at);
    if (!key) continue;
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  if (counts.size === 0) return [];
  const keys = [...counts.keys()].sort();
  const start = _fromDateKey(keys[0]);
  const end = _fromDateKey(keys[keys.length - 1]);
  const out = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    const k = `${cursor.getFullYear()}-${_pad(cursor.getMonth() + 1)}-${_pad(cursor.getDate())}`;
    out.push({
      date: k,
      label: cursor.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
      count: counts.get(k) || 0,
    });
    cursor.setDate(cursor.getDate() + 1);
  }
  // Cap at most 60 days to keep the chart readable
  return out.length > 60 ? out.slice(out.length - 60) : out;
}

export function groupByReviewer(items) {
  const counts = new Map();
  for (const it of items || []) {
    const name = it?.review_completed_by?.name || 'Unknown';
    counts.set(name, (counts.get(name) || 0) + 1);
  }
  return [...counts.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10); // top 10
}

export function groupByChartType(items) {
  const counts = new Map();
  for (const it of items || []) {
    const code = it?.chart_template_code || 'unknown';
    counts.set(code, (counts.get(code) || 0) + 1);
  }
  return [...counts.entries()]
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
}

export function computeHeadlineMetrics(items) {
  const total = items?.length || 0;
  const withNote = (items || []).filter(it => (it?.review_outcome_note || '').trim().length > 0).length;
  const reviewers = new Set((items || []).map(it => it?.review_completed_by?.id).filter(Boolean)).size;
  const participants = new Set((items || []).map(it => it?.participant_id).filter(Boolean)).size;
  // Average days from mark → completion (simple arithmetic mean, skip non-finite)
  const diffs = (items || []).map(it => {
    try {
      const mk = new Date(it?.review_marked_at).getTime();
      const cp = new Date(it?.review_completed_at).getTime();
      if (!isFinite(mk) || !isFinite(cp) || cp < mk) return null;
      return (cp - mk) / (1000 * 60 * 60 * 24);
    } catch { return null; }
  }).filter(v => v !== null);
  const avgDays = diffs.length ? (diffs.reduce((a, b) => a + b, 0) / diffs.length) : null;
  return {
    total,
    withNote,
    withNotePct: total ? Math.round((withNote / total) * 100) : 0,
    reviewers,
    participants,
    avgDays,
  };
}

export const CHART_TYPE_LABELS = {
  bowel_chart: 'Bowel Chart',
  unknown: 'Unknown',
};

export function prettyChartType(code) {
  return CHART_TYPE_LABELS[code] || (code || 'Unknown').split('_').map(w => w[0]?.toUpperCase() + w.slice(1)).join(' ');
}
